/**
 * Cell-level value transforms — convert a raw CSV/XLSX string into the typed
 * value the form engine + `submit_form` expect in `field_values`.
 *
 * Dates follow the storage invariant from jalali-date-utils: values are stored
 * as Gregorian ISO ('YYYY-MM-DD' / 'YYYY-MM-DDTHH:mm'). Source dates may be in
 * the Jalali calendar with an arbitrary format, parsed calendrically (no JS
 * Date / epoch) so there's no timezone drift.
 */
import { DateObject } from 'react-multi-date-picker'
import persian from 'react-date-object/calendars/persian'
import persian_fa from 'react-date-object/locales/persian_fa'
import gregorian from 'react-date-object/calendars/gregorian'
import gregorian_en from 'react-date-object/locales/gregorian_en'
import type { ColumnTransform } from './mapping-types'

export interface TransformResult {
  value: unknown
  error?: string
}

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'

/** Normalize Persian/Arabic-Indic digits to ASCII so number/date parsing works. */
export function normalizeDigits(s: string): string {
  return s.replace(/[۰-۹٠-٩]/g, (d) => {
    const p = PERSIAN_DIGITS.indexOf(d)
    if (p >= 0) return String(p)
    const a = ARABIC_DIGITS.indexOf(d)
    return a >= 0 ? String(a) : d
  })
}

export function applyTransform(rawInput: string, t: ColumnTransform): TransformResult {
  const raw = (rawInput ?? '').trim()
  if (raw === '') return { value: undefined }

  switch (t.kind) {
    case 'number': {
      const n = Number(normalizeDigits(raw).replace(/,/g, ''))
      if (Number.isNaN(n)) return { value: raw, error: `عدد نامعتبر: «${raw}»` }
      return { value: n }
    }
    case 'boolean': {
      const truthy = (t.trueValues ?? ['true', '1', 'yes']).map((v) => v.toLowerCase())
      return { value: truthy.includes(raw.toLowerCase()) }
    }
    case 'date': {
      return parseDate(raw, t)
    }
    case 'select': {
      const map = t.valueMap ?? {}
      if (t.multiDelimiter) {
        const parts = raw.split(t.multiDelimiter).map((p) => p.trim()).filter(Boolean)
        return { value: parts.map((p) => map[p] ?? p) }
      }
      return { value: map[raw] ?? raw }
    }
    case 'text':
    default:
      return { value: raw }
  }
}

function parseDate(raw: string, t: ColumnTransform): TransformResult {
  const cal = t.sourceCalendar === 'gregorian' ? gregorian : persian
  const loc = t.sourceCalendar === 'gregorian' ? gregorian_en : persian_fa
  const format = t.sourceFormat || (t.isDateTime ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD')
  const normalized = normalizeDigits(raw)
  let d: DateObject
  try {
    d = new DateObject({ calendar: cal, locale: loc, format, date: normalized })
  } catch {
    return { value: raw, error: `تاریخ نامعتبر: «${raw}»` }
  }
  if (!d.isValid) return { value: raw, error: `تاریخ نامعتبر: «${raw}»` }
  const g = d.convert(gregorian, gregorian_en)
  const datePart = g.format('YYYY-MM-DD')
  return { value: t.isDateTime ? `${datePart}T${g.format('HH:mm')}` : datePart }
}
